import { useBehavior } from '@libs/hooks';
import { AssetsImages, AssetsSvgMap, TMargin, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { MouseEventHandler, useMemo } from 'react';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { TVisualData } from '../../types/data';
import { TVisualOption } from '../../types/options';
import { capitalText, getInlineStyles } from '../../utils/styles';
import { Badge } from '../UI/Badge/Badge';
import { FlagIcon } from '../UI/FlagIcon/FlagIcon';
import { Img } from '../UI/Img/Img';
import styles from './styles/visual.module.css';

type TVisualProps<TD, TO> = (
    | {
          isWidget: false;
          dataTest?: string;
          iconName?: React.ReactNode;
          defaultIconName?: React.ReactNode;
          iconVariant?: 'simple' | 'circle';
          iconHierarchy?: TVisualOption['iconHierarchy'];
          size?: 'small' | 'medium';
          flagIcon?: string;
          flagIconSmall?: string;
          avatar?: string;
          base64?: string;
          rightMargin?: boolean;
          margin?: TMargin;
          /**
           * @deprecated
           */
          isEnabled?: boolean;
          isDisabled?: boolean;
          isSelected?: boolean;
      }
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      }
) & {
    handleClick?: MouseEventHandler;
};

const getFlagSize = (props: TVisualProps<TVisualData, TVisualOption>) => {
    if (props.isWidget) {
        return props.widget?.options.size === 'medium' ? '80px' : '40px';
    } else {
        return props.size === 'medium' ? '80px' : '40px';
    }
};
const getIconSize = <T,>(size?: T) => {
    switch (size) {
        case 'xxSmall':
            return 20;
        case 'medium':
            return 48;
        case 'large':
            return 80;
        default:
            return 24;
    }
};

const getIconWrapperSize = <T,>(size?: T) => {
    switch (size) {
        case 'xxSmall':
            return 20;
        case 'medium':
            return 80;
        case 'large':
            return 100;
        default:
            return 40;
    }
};
export const getIconColor = (disabled: boolean, iconHierarchy?: TVisualOption['iconHierarchy']) => {
    switch (iconHierarchy) {
        case 'emphasised':
            return 'white_base';
        case 'secondary':
            return disabled ? 'grey_40_lc' : 'grey_72_lc';
        case 'secondaryAlt':
            return disabled ? 'grey_40_lc' : 'black';
        case 'destructive':
            return disabled ? 'pink_40_lc' : 'pink_100';
        default:
            return disabled ? 'purple_40_tr' : 'purple_100';
    }
};

const getBase64 = (base64?: string) => {
    if (base64) {
        return base64.startsWith('data:image') ? base64 : 'data:image;base64,' + base64;
    }
    return undefined;
};

export function Visual(props: TVisualProps<TVisualData, TVisualOption>) {
    const { isWidget } = props;

    const finalWidget = useMemo(
        () =>
            isWidget
                ? props.widget
                : ({
                      uid: props.dataTest,
                      data: {
                          iconName: props.iconName,
                          defaultIconName: props.defaultIconName,
                          flagIcon: props.flagIcon,
                          flagIconSmall: props.flagIconSmall,
                          avatar: props.avatar,
                          base64: props.base64,
                      },
                      options: {
                          isDisabled: props.isDisabled ?? !props.isEnabled,
                          margin: props.margin,
                          size: props.size,
                          isSelected: props.isSelected,
                          iconVariant: props.iconVariant,
                          iconHierarchy: props.iconHierarchy,
                          isHide: false,
                      },
                  } as TWidget<TVisualData, TVisualOption>),
        [isWidget, props],
    );

    const { isDisabled } = useBehavior(finalWidget);

    const { iconName, defaultIconName, flagIcon, flagIconSmall, base64, avatar } = finalWidget.data;
    const { margin, size, iconHierarchy, iconVariant, isSelected } = finalWidget.options;

    const base64ForImage = getBase64(base64);

    const defaultIcon = AssetsSvgMap[defaultIconName as keyof typeof AssetsSvgMap] ?? AssetsSvgMap['ic_check'];
    const iconImage = AssetsImages[iconName as keyof typeof AssetsImages];

    const getVisual = () => {
        if (base64ForImage || avatar) {
            return (
                <span className={clsx(styles.avatarContainer, isDisabled && styles.disabled, size === 'medium' && styles.medium)}>
                    <Img
                        src={base64ForImage || avatar}
                        width={getIconSize(size)}
                        height={getIconSize(size)}
                        defaultIcon={defaultIcon}
                        fill={getIconColor(isDisabled, iconHierarchy)}
                    />
                </span>
            );
        } else if (iconName) {
            return (
                <span
                    className={clsx(
                        styles.iconContainer,
                        size !== 'xxSmall' && styles.notExtraSmall,
                        iconVariant === 'circle' && styles.borderColorCircle,
                        iconVariant === 'circle' && styles[`borderColor${capitalText(iconHierarchy ?? '')}`],
                        iconVariant === 'circle' && styles.backgroundColorCircle,
                        iconVariant === 'circle' && styles[`backgroundColor${capitalText(iconHierarchy ?? '')}`],
                        isDisabled && styles.disabled,
                    )}
                    style={getInlineStyles({
                        width: `${iconImage?.width ?? getIconWrapperSize(size)}px`,
                        height: `${iconImage?.height ?? getIconWrapperSize(size)}px`,
                    })}
                >
                    <SvgComponent
                        name={iconName ?? defaultIcon}
                        defaultName={defaultIcon}
                        width={iconImage?.width ?? getIconSize(size)}
                        height={iconImage?.height ?? getIconSize(size)}
                        fill={getIconColor(isDisabled, iconHierarchy)}
                    />
                </span>
            );
        } else if (flagIcon) {
            return (
                <FlagIcon
                    name={flagIcon}
                    size={getFlagSize(props)}
                    defaultIcon={defaultIcon}
                    fill={getIconColor(isDisabled, iconHierarchy)}
                    isDisabled={isDisabled}
                />
            );
        }
    };

    return (
        <span
            data-test={finalWidget.uid}
            className={clsx(styles.visualContainer, !isWidget && props.rightMargin && styles.marginRight)}
            style={getInlineStyles({ margin: margin })}
            onClick={props.handleClick}
            role="img"
        >
            {getVisual()}
            {flagIconSmall && (
                <span className={clsx(styles.badgeContainer, size === 'medium' && styles.medium)}>
                    <Badge flagIcon={flagIconSmall} size={size} disabled={isDisabled} defaultIcon={defaultIcon} />
                </span>
            )}
            {isSelected && (
                <div className={styles.selectedContainer}>
                    <SvgComponent name={AssetsSvgMap.ic_check} fill="purple_100" />
                </div>
            )}
        </span>
    );
}
